// utils/downscaleImage.ts — shrink photos under the scan v2 upload limits
// before they leave the device (a 12 MP phone photo is 6-8 MB).
import { loadDrawable, toBlobAsync } from './imageCanvas';

/** Identify photo limit (server: 2 MB). */
export const IDENTIFY_MAX_BYTES = 2 * 1024 * 1024;
/** Label photo limit per part (server: 5 MB). */
export const LABEL_PART_MAX_BYTES = 5 * 1024 * 1024;
/** Label photos limit in total (server: 12 MB). */
export const LABEL_TOTAL_MAX_BYTES = 12 * 1024 * 1024;

const IDENTIFY_LONG_EDGE = 1600;
const LABEL_LONG_EDGE = 2000;
const START_QUALITY = 0.85;
const MIN_QUALITY = 0.6;
const QUALITY_STEP = 0.1;
const EDGE_STEP = 0.8;
/** Below this long edge a label or product is no longer readable. */
const MIN_LONG_EDGE = 640;

/** The photo cannot be brought under the limit without becoming unreadable. */
export class ImageTooLargeError extends Error {
  constructor() {
    super('This photo is too large. Try another photo.');
    this.name = 'ImageTooLargeError';
  }
}

export const __testables = {
  createCanvas: (): HTMLCanvasElement => document.createElement('canvas'),
  loadDrawable,
};

type Drawable = ImageBitmap | HTMLImageElement;

function sizeOf(img: Drawable): { width: number; height: number } {
  const el = img as HTMLImageElement;
  return {
    width: el.naturalWidth || img.width,
    height: el.naturalHeight || img.height,
  };
}

async function encode(img: Drawable, longEdge: number, quality: number): Promise<Blob> {
  const { width, height } = sizeOf(img);
  const scale = Math.min(1, longEdge / Math.max(width, height));
  const canvas = __testables.createCanvas();
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context not available');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await toBlobAsync(canvas, 'image/jpeg', quality);
  if (!blob) throw new Error('Failed to encode the photo');
  return blob;
}

/**
 * Re-encode as JPEG at `longEdge` (never upscaled) and 0.85, then step the
 * quality down to 0.6, then the edge down by 20 %, until the output fits.
 */
async function shrinkTo(file: Blob, longEdge: number, maxBytes: number): Promise<Blob> {
  const img = await __testables.loadDrawable(file);
  try {
    const { width, height } = sizeOf(img);
    let edge = Math.min(longEdge, Math.max(width, height));
    let quality = START_QUALITY;
    for (;;) {
      const blob = await encode(img, edge, quality);
      if (blob.size <= maxBytes) return blob;
      if (quality > MIN_QUALITY) {
        quality = Math.max(MIN_QUALITY, Math.round((quality - QUALITY_STEP) * 100) / 100);
      } else {
        edge = Math.round(edge * EDGE_STEP);
        if (edge < MIN_LONG_EDGE) throw new ImageTooLargeError();
      }
    }
  } finally {
    if ('close' in img && typeof img.close === 'function') img.close();
  }
}

/** Identify photo: long edge 1600 px, ≤ 2 MB. */
export function downscaleForIdentify(file: Blob): Promise<Blob> {
  return shrinkTo(file, IDENTIFY_LONG_EDGE, IDENTIFY_MAX_BYTES);
}

/**
 * Label photos: long edge 2000 px, each ≤ 5 MB and all together ≤ 12 MB
 * (each part gets an equal share of the total when that is tighter).
 */
export async function downscaleLabelParts(files: Blob[]): Promise<Blob[]> {
  if (files.length === 0) return [];
  const perPart = Math.min(LABEL_PART_MAX_BYTES, Math.floor(LABEL_TOTAL_MAX_BYTES / files.length));
  const out: Blob[] = [];
  for (const file of files) {
    out.push(await shrinkTo(file, LABEL_LONG_EDGE, perPart));
  }
  return out;
}
